import { faCogs } from '@fortawesome/pro-light-svg-icons/faCogs';
import { faClipboard } from '@fortawesome/pro-solid-svg-icons/faClipboard';
import { faCopy } from '@fortawesome/pro-solid-svg-icons/faCopy';
import { faExternalLink } from '@fortawesome/pro-solid-svg-icons/faExternalLink';
import { faPage } from '@fortawesome/pro-solid-svg-icons/faPage';
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons/faPaperPlane';
import { faRectangleHistory } from '@fortawesome/pro-solid-svg-icons/faRectangleHistory';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { faWavePulse } from '@fortawesome/pro-solid-svg-icons/faWavePulse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	GetItemCounts,
	SendsPageItemType,
	updateSendsPageActiveStatus,
	useChangeSendsStatus,
	useInfiniteSendsPage,
} from '@ukpostbox/client-data';
import {
	Button,
	Colour,
	ColourOutline,
	ColourStateSolid,
	ContentLoader,
	ContentSidebarLayout,
	DropdownMenu,
	DropdownMenuEntry,
	FlexAlign,
	FlexGap,
	InfiniteLoadTrigger,
	ItemRow,
	ItemRowCell,
	ItemRowHeader,
	PageHeader,
	Pill,
	PillSize,
	RichEmptyState,
	StackHorizontal,
	StackVertical,
	ToggleSwitch,
	ToggleType,
	Tooltip,
	useLayout,
	useNotifications,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { ItemRowHeaderCell } from '@ukpostbox/frontend-framework/lib/Components/ItemRow/Cells/ItemRowHeaderCell';
import { GetInitials, ParseURL } from '@ukpostbox/helpers';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { EndpointError } from '../../../_Global/Components/EndpointError';
import SendsNavigation from '../../../SendMail/Components/Shared/Controls/SendsNavigation';
import ConfirmDeleteSendsPageModal from '../Components/Modals/ConfirmDeleteSendsPageModal';
import { useSite } from '../../../_Global/Providers/SiteInterfaceProvider';

function Profiles() {
	const { t } = useTranslation();
	const { currentLocale, updatePageMeta } = useSite();
	const navigate = useNavigate();

	const { isTiny, isSmall, isMedium, isLarge } = useLayout();
	const { AddNotification } = useNotifications();

	const { status, data, error, isSuccess, isFetchingNextPage, fetchNextPage, hasNextPage, isStale, isRefetching } =
		useInfiniteSendsPage();

	const [activeSendsPage, setActiveSendsPage] = useState<any>({ isOpen: false, pageData: null });
	const [itemCounts, setItemCounts] = useState<any>({ loaded: 0, total: 0 });

	const updateSendsStatus = useChangeSendsStatus();

	const sendsPagesRef = useRef<any>(null);
	const sendMailNavigationRef = useRef<any>(null);

	const HandleTemplateStatus = async (pageId: number, active: any) => {
		await updateSendsPageActiveStatus(pageId, active).then((resp) => {
			updateSendsStatus.mutate({
				newData: active,
				id: pageId,
			});
		});
	};

	const ShowDeleteConfirmation = (page: SendsPageItemType) => {
		setActiveSendsPage({ pageData: page, isOpen: true });
	};

	const HandleOpen = async (slug: string) => {
		window.open(ParseURL(slug), '_blank');
	};

	const HandleCopyUrl = async (slug: string) => {
		AddNotification({
			title: t('results.copiedClipboard'),
			icon: faClipboard,
		});
		await navigator.clipboard.writeText(ParseURL(slug));
	};

	useEffect(() => {
		if (data) {
			setItemCounts(GetItemCounts(data as any));
		}
	}, [data]);

	useEffect(() => {
		updatePageMeta({
			title: t('sends.profiles.title'),
		});
	}, [currentLocale]);

	return (
		<ContentSidebarLayout
			ref={sendsPagesRef}
			navigationComponent={
				<SendsNavigation
					ref={sendMailNavigationRef}
					title={t('sends.profiles.title')}
					addonContent={
						isSuccess &&
						(isTiny || isSmall || isMedium) && (
							<Button
								colour={ColourStateSolid.Primary}
								onClick={() => navigate('/send-mail/create')}
								squared
								title={t('sends.profiles.actions.new')}
								icon={faPage}
							/>
						)
					}
				/>
			}
			extraContentClass='flex-column'
		>
			{!isTiny && !isSmall && !isMedium && (
				<PageHeader title={t('sends.profiles.title')} secondaryHeader extraClass='section-page-header'>
					{isSuccess && (
						<Button
							colour={ColourStateSolid.Primary}
							onClick={() => navigate('/send-mail/create')}
							squared
							title={t('sends.profiles.actions.new')}
							icon={faPage}
						/>
					)}
				</PageHeader>
			)}

			{!isTiny && !isSmall && (
				<ItemRowHeader title='Send Profiles' sticky sidePadding extraClass='sends-templates-row'>
					<ItemRowHeaderCell>{t('sends.profiles.headings.name')}</ItemRowHeaderCell>
					<ItemRowHeaderCell>{t('sends.profiles.headings.users')}</ItemRowHeaderCell>
					<ItemRowHeaderCell>{t('sends.profiles.headings.actions')}</ItemRowHeaderCell>
					<ItemRowHeaderCell>{t('sends.profiles.headings.active')}</ItemRowHeaderCell>
				</ItemRowHeader>
			)}

			{status === 'pending' ? (
				<ContentLoader childHeight='5em' />
			) : status === 'error' ? (
				<EndpointError {...error} stacked extraClass='m-md' />
			) : (
				<>
					{data &&
						data.pages.map((group) =>
							group.data.map((pageEntry, index) => (
								<ItemRow
									title={`Send Profiles Entry ${pageEntry.id}`}
									key={pageEntry.id}
									sidePadding
									actionLink={{ url: `/send-mail/edit/${pageEntry.id}` }}
									extraClass='sends-templates-row'
								>
									{!isTiny && !isSmall ? (
										<>
											<ItemRowCell>{pageEntry.title}</ItemRowCell>
											<ItemRowCell>
												<StackHorizontal
													gap={FlexGap.ExtraSmall}
													wrap={pageEntry.users.length > 3}
												>
													{pageEntry.users.map((user) => {
														if (
															pageEntry.users.length <= 3 &&
															!isTiny &&
															!isSmall &&
															!isMedium &&
															!isLarge
														) {
															return (
																<Pill
																	squared
																	size={PillSize.Small}
																	key={`pageUser${pageEntry.id}`}
																	content={user.name}
																	colour={ColourOutline.Secondary}
																/>
															);
														}

														return (
															<Tooltip content={user.name}>
																<Pill
																	squared
																	size={PillSize.Small}
																	key={`pageUser${pageEntry.id}`}
																	content={GetInitials(user.name as string)}
																	colour={ColourOutline.Secondary}
																/>
															</Tooltip>
														);
													})}
												</StackHorizontal>
											</ItemRowCell>
											<ItemRowCell>
												<StackHorizontal gap={FlexGap.ExtraSmall} align={FlexAlign.Center}>
													<Button
														onClick={(e) => [
															e.preventDefault(),
															HandleOpen(pageEntry.liveUrl ? pageEntry.liveUrl : ''),
														]}
														colour={ColourStateSolid.Secondary}
														title={t('sends.profiles.actions.open')}
														iconOnly={isTiny || isSmall || isMedium || isLarge}
														icon={faExternalLink}
														squared
													/>
													<Button
														onClick={(e) => [
															e.preventDefault(),
															HandleCopyUrl(pageEntry.liveUrl ? pageEntry.liveUrl : ''),
														]}
														colour={ColourStateSolid.Secondary}
														title={t('sends.profiles.actions.copyUrl')}
														iconOnly={isTiny || isSmall || isMedium || isLarge}
														icon={faCopy}
														squared
													/>
													<Button
														onClick={(e) => [
															e.preventDefault(),
															ShowDeleteConfirmation(pageEntry),
														]}
														title={t('sends.profiles.actions.delete')}
														iconOnly={isTiny || isSmall || isMedium || isLarge}
														icon={faTrash}
														squared
													/>
												</StackHorizontal>
											</ItemRowCell>

											<ItemRowCell>
												<ToggleSwitch
													type={ToggleType.Switch}
													hideLabel
													name={`sendsPageEntry${pageEntry.id}`}
													label={t('sends.profiles.heading.active')}
													defaultChecked={pageEntry.isActive}
													onChange={(e: any) =>
														HandleTemplateStatus(
															pageEntry.id ? pageEntry.id : -1,
															e.currentTarget.checked,
														)
													}
												/>
											</ItemRowCell>
										</>
									) : (
										<>
											<StackVertical>
												<div className='flex-row align-center flex-gap-xs'>
													<div className='flex-column flex-gap-xs'>
														<h5 className='m-0'>{pageEntry.title}</h5>
													</div>
												</div>
												<StackHorizontal gap={FlexGap.ExtraSmall}>
													{pageEntry.users.map((user) => {
														if (
															pageEntry.users.length <= 3 &&
															!isTiny &&
															!isSmall &&
															!isMedium &&
															!isLarge
														) {
															return (
																<Pill
																	squared
																	key={`pageUser${pageEntry.id}`}
																	content={user.name}
																	colour={Colour.Secondary}
																/>
															);
														}

														return (
															<Tooltip content={user.name}>
																<Pill
																	squared
																	key={`pageUser${pageEntry.id}`}
																	content={GetInitials(user.name as string)}
																	colour={Colour.Secondary}
																/>
															</Tooltip>
														);
													})}
												</StackHorizontal>
											</StackVertical>

											<DropdownMenu
												title='Send Profiles'
												trigger={
													<Button
														title={t('actions.title')}
														squared
														icon={faCogs}
														iconOnly
														onClick={() => null}
													/>
												}
											>
												<DropdownMenuEntry
													title={t('sends.profilesHistory.title')}
													onClick={() =>
														navigate(`/send-mail/history`, {
															state: { pageIds: pageEntry.id },
														})
													}
													icon={faRectangleHistory}
												/>

												<DropdownMenuEntry title={t('divider')} sectionDivider />

												<DropdownMenuEntry
													title={t('sends.profiles.actions.open')}
													onClick={() =>
														HandleOpen(pageEntry.liveUrl ? pageEntry.liveUrl : '')
													}
													icon={faExternalLink}
												/>

												<DropdownMenuEntry
													title={t('sends.profiles.actions.copyUrl')}
													onClick={() =>
														HandleCopyUrl(pageEntry.liveUrl ? pageEntry.liveUrl : '')
													}
													icon={faCopy}
												/>

												<DropdownMenuEntry title={t('divider')} sectionDivider />

												<DropdownMenuEntry
													title={t('sends.profiles.actions.active')}
													icon={faWavePulse}
													onClick={() =>
														HandleTemplateStatus(
															pageEntry.id ? pageEntry.id : -1,
															!pageEntry.isActive,
														)
													}
													addonContentEnd={
														<ToggleSwitch
															type={ToggleType.Switch}
															hideLabel
															label={t('sends.profiles.actions.active')}
															name={`isActive${pageEntry.id}`}
															defaultChecked={pageEntry.isActive}
														/>
													}
												/>

												<DropdownMenuEntry
													title={t('sends.profiles.actions.delete')}
													onClick={() => ShowDeleteConfirmation(pageEntry)}
													icon={faTrash}
												/>
											</DropdownMenu>
										</>
									)}
								</ItemRow>
							)),
						)}

					{itemCounts.total === 0 && (
						<RichEmptyState
							title={t('sends.profiles.noData.title')}
							description={t('sends.profiles.noData.description')}
							media={<FontAwesomeIcon icon={faPaperPlane} size='3x' />}
							addon={
								<Button
									title={t('sends.profiles.actions.getStarted')}
									colour={ColourStateSolid.Primary}
									onClick={() => navigate('/send-mail/create')}
								/>
							}
						/>
					)}

					{itemCounts.total > 0 && (
						<InfiniteLoadTrigger
							loaded={itemCounts.loaded}
							totalCount={itemCounts.total}
							hasNextPage={hasNextPage}
							fetchNextPage={fetchNextPage}
							isStale={isStale}
							isRefetching={isRefetching}
							isFetchingNextPage={isFetchingNextPage}
						/>
					)}
				</>
			)}

			<ConfirmDeleteSendsPageModal
				pageData={activeSendsPage.pageData}
				isOpen={activeSendsPage.isOpen}
				closeCallback={() => setActiveSendsPage({ ...activeSendsPage, isOpen: false })}
			/>
		</ContentSidebarLayout>
	);
}

export default Profiles;
